<template>
    <v-dialog
        v-model="dialog"
        persistent
        eager
        :max-width="dialogWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card role="region" aria-label="device-selection-dialog">
            <v-card-title class="py-2">
                {{ dialogTitle }}
            </v-card-title>
            <v-card-text class="px-4 pb-1">
                <v-container px-0 pb-1 fluid>
                    <v-row no-gutters>
                        <v-col>
                            <v-btn-toggle
                                v-model="assignedFilter"
                                dense
                                mandatory
                                color="accent"
                                group
                            >
                                <v-btn value="unassigned">Unassigned</v-btn>
                                <v-btn value="all">All</v-btn>
                            </v-btn-toggle>
                        </v-col>
                        <v-col>
                            <v-text-field
                                :value="queryText"
                                dense
                                clearable
                                label="Quick filter"
                                :loading="loading"
                                :error-messages="fetchErrors"
                                @input="onQueryTextInput"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-container>
                <v-card elevation="10">
                    <v-card-text class="py-0 px-1">
                        <v-data-table
                            v-model="selectedItems"
                            fixed-header
                            height="300px"
                            hide-default-footer
                            show-select
                            :headers="tableHeaders"
                            :items="filteredDevices"
                            :items-per-page="200"
                            :page="1"
                            :loading="loading"
                        >
                            <template v-slot:item.healthCheckStatus="{ item }">
                                <device-health-icon
                                    :value="{
                                        status: item.healthCheckStatus || '',
                                        statusDate:
                                            item.healthCheckStatusDate || ''
                                    }"
                                >
                                </device-health-icon>
                            </template>
                            <template v-slot:no-data>
                                <div class="warning--text text-center">
                                    No data found...
                                </div>
                            </template>
                            <!-- =============== The select all checkbox ================== -->
                            <template
                                v-slot:header.data-table-select="{ props }"
                            >
                                <v-checkbox
                                    :value="props.value"
                                    :indeterminate="props.indeterminate"
                                    hide-details
                                    class="mt-0"
                                    :color="
                                        $vuetify.theme.isDark
                                            ? 'white'
                                            : 'grey darken-1'
                                    "
                                    @change="onSelectAllChange"
                                ></v-checkbox>
                            </template>
                            <!-- ================== the item selection box ================ -->
                            <template
                                v-slot:item.data-table-select="{
                                    item,
                                    isSelected
                                }"
                            >
                                <v-checkbox
                                    :value="isSelected"
                                    hide-details
                                    class="mt-0"
                                    :color="
                                        $vuetify.theme.isDark
                                            ? 'white'
                                            : 'grey darken-1'
                                    "
                                    @change="
                                        onItemSelect({
                                            item,
                                            value: !isSelected
                                        })
                                    "
                                ></v-checkbox>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-card-text>
            <v-card-actions class="px-4 justify-end">
                <v-btn small outlined class="mr-3" @click="onCancelClick"
                    >Cancel</v-btn
                >
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            small
                            color="primary"
                            :disabled="!allowAddButton"
                            v-on="on"
                            @click="onAddClick"
                            >Add</v-btn
                        >
                    </template>
                    <span>Add the selected devices</span>
                </v-tooltip>
            </v-card-actions>
            <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { debounce } from 'debounce';
import DeviceHealthIcon from '@/components/Fields/DeviceHealthIcon';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';

export default {
    name: 'DeviceSelectionDialog',
    components: {
        'device-health-icon': DeviceHealthIcon,
        'confirmation-dialog': ConfirmationDialog
    },
    data() {
        return {
            dialog: false,
            resolve: null,
            reject: null,
            title: 'Select a device',
            excludeRows: [],
            options: {
                width: '850'
            },
            assignedFilter: 'unassigned',
            queryText: '',
            tableHeaders: [
                { text: 'Name', value: 'name', sortable: true },
                { text: 'Mac Address', value: 'hostMac', sortable: true },
                { text: 'Serial NO.', value: 'serialNumber', sortable: true },
                { text: 'Health', value: 'healthCheckStatus', sortable: false },
                {
                    text: 'ServiceAccount',
                    value: 'serviceAccount.name',
                    sortable: false
                }
            ],
            devices: [],
            loading: false,
            selectedItems: [],
            fetchErrors: null
        };
    },
    computed: {
        ...mapGetters('spaces', ['getSpaceByServiceAccount']),
        filteredDevices() {
            let result = [];
            // apply the assigned filter first
            if (this.assignedFilter === 'unassigned') {
                result = this.devices.filter(item => {
                    return !item.serviceAccount;
                });
            } else {
                result = [...this.devices];
            }
            // apply the exclude rows filter
            if (this.excludeRows && this.excludeRows.length) {
                result = result.filter(item => {
                    return !this.excludeRows.includes(item.serialNumber);
                });
            }
            return result;
        },
        dialogTitle() {
            if (this.filteredDevices && this.filteredDevices.length) {
                return `${this.title} (${this.filteredDevices.length})`;
            } else {
                return this.title;
            }
        },
        dialogWidth() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return '90%';
            } else {
                return this.options.width;
            }
        },
        allowAddButton() {
            return this.selectedItems.length > 0;
        }
    },
    methods: {
        // ================= Events =========================
        ...mapActions('devices', ['fetchItems', 'fetchDevicesFromCDS']),
        onQueryTextInput: debounce(function(value) {
            this.queryText = value;
            this.findDevices(value);
        }, 600),
        onCancelClick() {
            this.resolve(false);
            this.clearFilters();
            this.dialog = false;
        },
        onAddClick() {
            // need to add confirmation if the user adds all devices
            if (this.selectedItems.length >= 20) {
                this.$refs.confirmationDialog
                    .open(
                        'Add Devices',
                        `Are you sure you want to add all selected devices (${this.selectedItems.length} device) to this space?`,
                        '',
                        { showIcon: true }
                    )
                    .then(res => {
                        if (res) {
                            this.resolveDialog(this.selectedItems);
                        }
                    });
            } else {
                this.resolveDialog(this.selectedItems);
            }
        },
        onEscapeKeydown() {
            this.onCancelClick();
        },
        onItemSelect({ item, value }) {
            if (value) {
                this.selectedItems.push(item);
            } else {
                this.selectedItems = this.selectedItems.filter(
                    elem => elem.id !== item.id
                );
            }
        },
        onSelectAllChange(value) {
            if (value) {
                this.selectedItems = [...this.filteredDevices];
            } else {
                this.selectedItems = [];
            }
        },
        // ================= Others ============================
        open(title, excludeRows, options) {
            // excludeRows: array of device serial numbers
            this.dialog = true;
            this.title = title;
            if (excludeRows && Array.isArray(excludeRows)) {
                this.excludeRows = excludeRows;
            } else {
                this.excludeRows = [];
            }
            this.options = { ...this.options, ...options };
            this.findDevices('');
            return new Promise((resolve, reject) => {
                this.resolve = resolve;
                this.reject = reject;
            });
        },
        clearFilters() {
            this.assignedFilter = 'unassigned';
            this.queryFilter = '';
            this.selectedItems = [];
        },
        findDevices(query) {
            this.loading = true;
            this.fetchErrors = '';
            const searchParams = {
                size: 20,
                number: 1,
                q: query,
                sort: 'added',
                desc: true
            };
            this.fetchItems({ searchParams })
                .then(res => {
                    this.devices = res.data.content;
                })
                .catch(() => {
                    this.fetchErrors = `Error occured while fetching devices. Please try again later.`;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        itemTooltip(item) {
            if (item) {
                return this.itemSpecs(item);
            } else {
                return 'No info found!';
            }
        },
        itemSpecs(item) {
            let location = 'ID: ' + item.id;
            if (item) {
                if (item.serviceAccount) {
                    location = ' - deviceXbo:' + item.xboDeviceId;
                }
                if (item.make) {
                    location += ' - ' + item.make;
                }
                if (item.model) {
                    location += ' - ' + item.model;
                }
                if (item.type) {
                    location += ' - ' + item.type;
                }
                if (item.status) {
                    location += ' - ' + item.status;
                }
            }
            return location;
        },
        resolveDialog(selectedDevices) {
            if (selectedDevices.length) {
                this.fetchDevicesFromCDS({
                    devices: selectedDevices
                }).then(cdsDevices => {
                    this.resolve(cdsDevices);
                    this.clearFilters();
                    this.dialog = false;
                });
            } else {
                this.resolve(false);
                this.clearFilters();
                this.dialog = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
tbody tr {
    cursor: pointer;
}
</style>
